import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { usePurchaseOrderCsvUpload } from '../usePurchaseOrderCsvUpload';
import { cn } from '@/lib/utils';

interface PreviewStepProps {
  onSubmit: () => void;
}

const PreviewStep: React.FC<PreviewStepProps> = ({ onSubmit }) => {
  const { previewRows, setStep, setValidationErrors } = usePurchaseOrderCsvUpload();
  const { format } = useCurrencyFormatter();
  const { t } = useTranslation('inventory');

  const summary = useMemo(() => {
    let totalUnits = 0;
    let totalCost = 0;
    let hasCostData = false;

    for (const row of previewRows) {
      totalUnits += row.quantity;
      if (row.totalCostImpact != null) {
        totalCost += row.totalCostImpact;
        hasCostData = true;
      }
    }

    return {
      totalUnits,
      skuCount: previewRows.length,
      totalCost,
      hasCostData,
    };
  }, [previewRows]);

  // BLU-373 PR2b: variance detection — flag any row whose unit cost is more
  // than 2x or less than 0.5x the median unit cost across the import. This is
  // a within-import outlier check: catches "everything is $11 except one
  // mistake row at $80" without needing external category-cost reference data.
  const variance = useMemo(() => {
    const costs = previewRows
      .map((r) => r.unitCost)
      .filter((c): c is number => c != null && c > 0)
      .sort((a, b) => a - b);

    if (costs.length < 4) {
      return { median: null, outlierSkus: new Set<string>() };
    }

    // Proper median for both odd and even length arrays.
    const mid = Math.floor(costs.length / 2);
    const median = costs.length % 2 === 0
      ? ((costs[mid - 1] ?? 0) + (costs[mid] ?? 0)) / 2
      : (costs[mid] ?? 0);
    if (median <= 0) {
      return { median: null, outlierSkus: new Set<string>() };
    }
    const lowerBound = median * 0.5;
    const upperBound = median * 2;
    const outlierSkus = new Set<string>();

    for (const row of previewRows) {
      if (row.unitCost == null || row.unitCost <= 0) continue;
      if (row.unitCost < lowerBound || row.unitCost > upperBound) {
        outlierSkus.add(row.sku);
      }
    }

    return { median, outlierSkus };
  }, [previewRows]);

  // BLU-373 PR2b: first-PO-at-new-location signal — every row is new
  const allNewAtLocation =
    previewRows.length > 0 && previewRows.every((r) => r.isNewAtLocation);

  const handleChangeFile = () => {
    setValidationErrors([]);
    setStep('upload');
  };

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-2 text-sm px-1 flex-wrap">
        <span>
          Ordering <strong>{summary.totalUnits.toLocaleString()}</strong> units
          across <strong>{summary.skuCount}</strong> SKU
          {summary.skuCount !== 1 ? 's' : ''}
        </span>
        {summary.hasCostData && (
          <>
            <span className="text-muted-foreground">|</span>
            <span>
              Est. total: <strong>{format(summary.totalCost)}</strong>
            </span>
          </>
        )}
      </div>

      {/* BLU-373 PR2b: first-PO-at-new-location banner */}
      {allNewAtLocation && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="text-xs">
            {t('submitOrder.csvNewAtLocationNote')}
          </AlertDescription>
        </Alert>
      )}

      {/* BLU-373 PR2b: variance flag — outlier unit costs vs the median in this import */}
      {variance.outlierSkus.size > 0 && variance.median != null && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-900">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="text-xs">
            <strong>{variance.outlierSkus.size}</strong> row
            {variance.outlierSkus.size !== 1 ? 's have' : ' has'} unit cost more than 2× off the
            median ({format(variance.median)}). Flagged in the table, double-check before applying.
          </AlertDescription>
        </Alert>
      )}

      {/* Preview table */}
      <div className="border rounded-md max-h-[340px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                SKU
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                Current Stock
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                Order Qty
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground hidden sm:table-cell">
                Unit Cost
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground hidden sm:table-cell">
                Line Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {previewRows.map((row) => (
              <tr key={row.sku} className="hover:bg-muted/50">
                <td className="px-3 py-1.5 font-mono text-xs">
                  <span className="flex items-center gap-1.5 flex-wrap">
                    {row.sku}
                    {row.isNewAtLocation && (
                      <Badge
                        variant="default"
                        size="sm"
                        className="bg-threadheads-purple/10 text-threadheads-purple border-threadheads-purple/20"
                      >
                        New at location
                      </Badge>
                    )}
                    {row.rowCount > 1 && (
                      <Badge
                        variant="outline"
                        size="sm"
                        className="text-muted-foreground"
                      >
                        merged from {row.rowCount} rows
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs">
                  {row.isNewAtLocation ? (
                    <span className="text-muted-foreground">--</span>
                  ) : (
                    row.currentStock
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs text-green-600 font-medium">
                  {row.quantity}
                </td>
                <td
                  className={cn(
                    'px-3 py-1.5 text-right tabular-nums text-xs hidden sm:table-cell',
                    variance.outlierSkus.has(row.sku)
                      ? 'text-amber-700 font-medium'
                      : 'text-muted-foreground',
                  )}
                  title={variance.outlierSkus.has(row.sku) ? 'Outlier vs median in this import' : undefined}
                >
                  {variance.outlierSkus.has(row.sku) && (
                    <>
                      <span className="sr-only">Outlier: </span>
                      <AlertTriangle className="inline-block h-3 w-3 mr-1 -mt-0.5" aria-hidden="true" />
                    </>
                  )}
                  {row.unitCost != null ? format(row.unitCost) : '--'}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-xs hidden sm:table-cell">
                  {row.totalCostImpact != null ? (
                    <span>{format(row.totalCostImpact)}</span>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={handleChangeFile}>
          Change File
        </Button>
        <Button
          onClick={onSubmit}
          className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
        >
          Apply {previewRows.length} Item{previewRows.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
};

export default PreviewStep;
